/**
 * Utilitas kompresi gambar berbasis browser (Client-Side Pre-Compression).
 * Menggunakan HTML5 Canvas API untuk me-resize dimensi berlebih dan mengonversi format ke WebP.
 *
 * Keuntungan:
 * 1. 0% Beban CPU Server (proses kompresi dilakukan di memori browser pengguna).
 * 2. Upload jauh lebih cepat (ukuran file berkurang hingga 85% - 95%).
 * 3. Mempertahankan ketajaman visual untuk teks/diagram/skema arsitektur.
 */
const DEFAULT_OPTIONS = {
    maxWidth: 1920,
    maxHeight: 1920,
    quality: 0.82,
    targetFormat: "image/webp",
};
export async function compressImage(file, customOptions) {
    const options = { ...DEFAULT_OPTIONS, ...customOptions };
    // 1. Lewatkan (bypass) file jika bukan gambar atau jika merupakan GIF / SVG (agar animasi/vektor tidak rusak)
    if (!file.type.startsWith("image/") || file.type === "image/gif" || file.type === "image/svg+xml") {
        return file;
    }
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                let width = img.naturalWidth || img.width;
                let height = img.naturalHeight || img.height;
                // 2. Hitung dimensi proporsional jika melebihi batas maksimum
                if (width > options.maxWidth || height > options.maxHeight) {
                    if (width > height) {
                        height = Math.round((height * options.maxWidth) / width);
                        width = options.maxWidth;
                    }
                    else {
                        width = Math.round((width * options.maxHeight) / height);
                        height = options.maxHeight;
                    }
                }
                // 3. Render ke Canvas
                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d", { alpha: true });
                if (!ctx) {
                    // Fallback jika canvas context tidak tersedia
                    resolve(file);
                    return;
                }
                // Smoothing quality prima
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = "high";
                ctx.drawImage(img, 0, 0, width, height);
                // 4. Export ke Blob WebP
                canvas.toBlob((blob) => {
                    if (!blob) {
                        resolve(file);
                        return;
                    }
                    // Jika hasil kompresi ternyata lebih besar dari file asli (kasus gambar sangat kecil), pakai file asli
                    if (blob.size >= file.size && file.type === options.targetFormat) {
                        resolve(file);
                        return;
                    }
                    // Bentuk nama file baru dengan ekstensi .webp
                    const baseName = file.name.replace(/\.[^/.]+$/, "");
                    const newFileName = `${baseName}.webp`;
                    const compressedFile = new File([blob], newFileName, {
                        type: options.targetFormat,
                        lastModified: Date.now(),
                    });
                    resolve(compressedFile);
                }, options.targetFormat, options.quality);
            };
            img.onerror = () => {
                // Jika format gambar gagal di-decode, kembalikan file asli tanpa blokir proses
                resolve(file);
            };
            img.src = event.target?.result;
        };
        reader.onerror = () => {
            resolve(file);
        };
        reader.readAsDataURL(file);
    });
}
